import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import {
  RateReview as ReviewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  EventRepeat as ExtendIcon,
  Block as RevokeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import DatePicker from '../../../components/DatePicker';
import { formatDateWithCulture } from '../../../utils/dateUtils';
import {
  LICENSE_STATUS_WAITING,
  LICENSE_STATUS_APPROVE,
  LICENSE_STATUS_REJECT,
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_REVOKED,
} from '../../../constants';

function ReviewLicenseModal({
  open,
  onClose,
  licenseData = null,
  licenseStatuses = [],
  onApprove,
  onReject,
  onExtend,
  onRevoke,
  i18n,
}) {
  const { t } = useTranslation();
  const [confirmAction, setConfirmAction] = useState(null); // 'reject', 'revoke', or 'extend'
  const [statusNote, setStatusNote] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');

  if (!licenseData) return null;

  const statusObj = licenseStatuses.find((status) => status.id === licenseData.status);
  const statusName = statusObj ? statusObj.name : '-';

  // Determine which buttons to show based on status
  const currentStatus = licenseData.status;
  const canApprove = currentStatus === LICENSE_STATUS_WAITING || currentStatus === LICENSE_STATUS_APPROVE;
  const canReject = currentStatus === LICENSE_STATUS_WAITING || currentStatus === LICENSE_STATUS_APPROVE;
  const canRevoke = currentStatus === LICENSE_STATUS_ACTIVE;
  // Extend is allowed for Active, Rejected, Expired, or Revoked (to reactivate)
  const canExtend = currentStatus === LICENSE_STATUS_ACTIVE ||
    currentStatus === LICENSE_STATUS_REJECT ||
    currentStatus === LICENSE_STATUS_EXPIRED ||
    currentStatus === LICENSE_STATUS_REVOKED;
  // Check if this is a reactivation (from Rejected, Expired, or Revoked)
  const isReactivation = currentStatus === LICENSE_STATUS_REJECT ||
    currentStatus === LICENSE_STATUS_EXPIRED ||
    currentStatus === LICENSE_STATUS_REVOKED;

  const handleApprove = () => {
    onApprove(licenseData.id);
  };

  const handleReject = () => {
    setConfirmAction('reject');
    setStatusNote('');
  };

  const handleExtend = () => {
    setConfirmAction('extend');
    // Set default new expiry date to 1 year from today
    const defaultNewExpiry = new Date();
    defaultNewExpiry.setFullYear(defaultNewExpiry.getFullYear() + 1);
    // Format as YYYY-MM-DD string for DatePicker component
    const formattedDate = `${defaultNewExpiry.getFullYear()}-${String(defaultNewExpiry.getMonth() + 1).padStart(2, '0')}-${String(defaultNewExpiry.getDate()).padStart(2, '0')}`;
    setNewExpiryDate(formattedDate);
  };

  const handleRevoke = () => {
    setConfirmAction('revoke');
    setStatusNote('');
  };

  const handleConfirmAction = () => {
    if (confirmAction === 'reject') {
      onReject(licenseData.id, statusNote);
    } else if (confirmAction === 'revoke') {
      onRevoke(licenseData.id, statusNote);
    } else if (confirmAction === 'extend') {
      onExtend(licenseData.id, newExpiryDate);
    }
    setConfirmAction(null);
    setStatusNote('');
    setNewExpiryDate('');
    onClose();
  };

  const handleCancelAction = () => {
    setConfirmAction(null);
    setStatusNote('');
    setNewExpiryDate('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <ReviewIcon />
        {t('licenses.reviewDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {t('licenses.reviewDialog.licenseDetails')}
          </Typography>

          <Grid container spacing={3}>
            {/* Code */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.code')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.code || '-'}
              </Typography>
            </Grid>

            {/* Key */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.key')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.key || '-'}
              </Typography>
            </Grid>

            {/* Unit Name */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.unitName')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.unitName || '-'}
              </Typography>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.description')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.description || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Issue Date */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.issueDate')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDateWithCulture(licenseData.issueDate, i18n.language) || '-'}
              </Typography>
            </Grid>

            {/* Expiry Date */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.expiryDate')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDateWithCulture(licenseData.expiryDate, i18n.language) || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.status')}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mt: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  backgroundColor: statusObj ? '#e3f2fd' : '#e2e8f0',
                  color: statusObj ? '#1565c0' : '#64748b',
                }}
              >
                {statusName}
              </Box>
            </Grid>

            {/* Max Users */}
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.maxUsers')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.maxUsers || '-'}
              </Typography>
            </Grid>

            {/* Max Devices */}
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.maxDevices')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.maxDevices || '-'}
              </Typography>
            </Grid>

            {/* Max Group Device */}
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                {t('licenses.table.maxGroupDevice')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {licenseData.maxGroupDevice || '-'}
              </Typography>
            </Grid>
          </Grid>

          {/* Status Note - Show at bottom when license is Rejected, Expired, or Revoked */}
          {isReactivation && licenseData.statusNote && (
            <Alert
              severity={currentStatus === LICENSE_STATUS_REJECT ? 'error' : 'warning'}
              sx={{ mt: 3 }}
            >
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                {currentStatus === LICENSE_STATUS_REJECT
                  ? t('licenses.reviewDialog.rejectionReason')
                  : currentStatus === LICENSE_STATUS_REVOKED
                    ? t('licenses.reviewDialog.revocationReason')
                    : t('licenses.reviewDialog.expirationNote')}
              </Typography>
              <Typography variant="body2">
                {licenseData.statusNote}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
        {confirmAction ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t(`licenses.reviewDialog.${confirmAction}ReasonPrompt`)}
            </Typography>
            {confirmAction === 'extend' ? (
              <DatePicker
                label={isReactivation ? t('licenses.reviewDialog.newExpiryDateReactivate') : t('licenses.reviewDialog.newExpiryDate')}
                value={newExpiryDate}
                onChange={(e) => setNewExpiryDate(e.target.value)}
                minDate={(() => {
                  // For reactivation (Rejected, Expired, Revoked), use tomorrow as min date
                  if (isReactivation) {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    return `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
                  }
                  // For normal extend (Active), use expiryDate + 1
                  if (licenseData?.expiryDate) {
                    // Parse date avoiding timezone issues
                    const datePart = licenseData.expiryDate.split('T')[0];
                    const [year, month, day] = datePart.split('-').map(Number);
                    // Add 1 day to current expiry date
                    const minDateObj = new Date(year, month - 1, day + 1);
                    return `${minDateObj.getFullYear()}-${String(minDateObj.getMonth() + 1).padStart(2, '0')}-${String(minDateObj.getDate()).padStart(2, '0')}`;
                  }
                  // Fallback to tomorrow
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  return `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
                })()}
                maxDate="2099-12-31"
                required
                id="newExpiryDate"
                name="newExpiryDate"
                data-testid="review-modal-input-newExpiryDate"
                sx={{ mb: 2 }}
              />
            ) : (
              <TextField
                label={t('licenses.reviewDialog.statusNote')}
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                multiline
                rows={3}
                fullWidth
                required
                inputProps={{
                  id: 'statusNote',
                  name: 'statusNote',
                  'data-testid': 'review-modal-input-statusNote',
                  maxLength: 500,
                }}
                sx={{ mb: 2 }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCancelAction}
                variant="outlined"
                id="cancelAction"
                name="cancelAction"
                data-testid="review-modal-button-cancelAction"
              >
                {t('licenses.reviewDialog.cancel')}
              </Button>
              <Button
                onClick={handleConfirmAction}
                variant="contained"
                color={confirmAction === 'extend' ? 'warning' : 'error'}
                disabled={confirmAction === 'extend' ? !newExpiryDate : !statusNote.trim()}
                id="confirmAction"
                name="confirmAction"
                data-testid="review-modal-button-confirmAction"
              >
                {t(`licenses.reviewDialog.confirm${confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)}`)}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canApprove && (
                <Button
                  onClick={handleApprove}
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  id="approve"
                  name="approve"
                  data-testid="review-modal-button-approve"
                >
                  {t('licenses.reviewDialog.approve')}
                </Button>
              )}
              {canReject && (
                <Button
                  onClick={handleReject}
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  id="reject"
                  name="reject"
                  data-testid="review-modal-button-reject"
                >
                  {t('licenses.reviewDialog.reject')}
                </Button>
              )}
              {canRevoke && (
                <Button
                  onClick={handleRevoke}
                  variant="contained"
                  color="error"
                  startIcon={<RevokeIcon />}
                  id="revoke"
                  name="revoke"
                  data-testid="review-modal-button-revoke"
                >
                  {t('licenses.reviewDialog.revoke')}
                </Button>
              )}
              {canExtend && (
                <Button
                  onClick={handleExtend}
                  variant="contained"
                  color={isReactivation ? 'success' : 'warning'}
                  startIcon={<ExtendIcon />}
                  id="extend"
                  name="extend"
                  data-testid="review-modal-button-extend"
                >
                  {isReactivation ? t('licenses.reviewDialog.reactivate') : t('licenses.reviewDialog.extend')}
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={onClose}
                variant="outlined"
                startIcon={<CloseIcon />}
                id="exit"
                name="exit"
                data-testid="review-modal-button-exit"
              >
                {t('licenses.reviewDialog.exit')}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ReviewLicenseModal;
